import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ArrowRight } from 'lucide-react';
import type { SetContentItem } from '@/types';

interface SetContentsSectionProps {
    contents: SetContentItem[];
    totalPieces?: number;
}

export const SetContentsSection = ({ contents, totalPieces }: SetContentsSectionProps) => {
    if (!contents || contents.length === 0) return null;

    // Group by role
    const tables = contents.filter(item => item.role === 'table');
    const chairs = contents.filter(item => item.role === 'chair');

    return (
        <div className="bg-gradient-to-br from-stone-50 to-stone-100/50 rounded-xl p-6 border border-stone-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-charcoal/5 flex items-center justify-center">
                        <Package className="w-5 h-5 text-charcoal" />
                    </div>
                    <div>
                        <h3 className="font-display text-lg text-charcoal">Takım İçeriği</h3>
                        <p className="text-xs text-gray-500">
                            {totalPieces ? `${totalPieces} parça` : `${contents.reduce((sum, c) => sum + c.quantity, 0)} parça`}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content List */}
            <div className="space-y-4">
                {/* Tables */}
                {tables.map((item, index) => (
                    <motion.div
                        key={`table-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center gap-4 p-3 bg-white rounded-lg border border-stone-100 hover:border-stone-200 hover:shadow-sm transition-all group"
                    >
                        {/* Image */}
                        <div className="w-16 h-16 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                            {item.productImage ? (
                                <img
                                    src={item.productImage}
                                    alt={item.productTitle}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                    <Package className="w-6 h-6" />
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider rounded">
                                    Masa
                                </span>
                                <span className="text-xs text-gray-400">× {item.quantity}</span>
                            </div>
                            <h4 className="font-medium text-charcoal truncate">{item.productTitle}</h4>
                        </div>

                        {/* Link */}
                        {item.productSlug && (
                            <Link
                                to={`/products/${item.productSlug}`}
                                className="p-2 rounded-full bg-stone-100 text-gray-400 hover:bg-charcoal hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                            >
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        )}
                    </motion.div>
                ))}

                {/* Chairs */}
                {chairs.map((item, index) => (
                    <motion.div
                        key={`chair-${index}`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: (tables.length + index) * 0.1 }}
                        className="flex items-center gap-4 p-3 bg-white rounded-lg border border-stone-100 hover:border-stone-200 hover:shadow-sm transition-all group"
                    >
                        {/* Image */}
                        <div className="w-16 h-16 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                            {item.productImage ? (
                                <img
                                    src={item.productImage}
                                    alt={item.productTitle}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                    <Package className="w-6 h-6" />
                                </div>
                            )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold uppercase tracking-wider rounded">
                                    Sandalye
                                </span>
                                <span className="text-xs text-gray-400">× {item.quantity}</span>
                            </div>
                            <h4 className="font-medium text-charcoal truncate">{item.productTitle}</h4>
                        </div>

                        {/* Link */}
                        {item.productSlug && (
                            <Link
                                to={`/products/${item.productSlug}`}
                                className="p-2 rounded-full bg-stone-100 text-gray-400 hover:bg-charcoal hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                            >
                                <ArrowRight className="w-4 h-4" />
                            </Link>
                        )}
                    </motion.div>
                ))}
            </div>

            {/* Summary Footer */}
            <div className="mt-4 pt-4 border-t border-stone-200">
                <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Toplam Parça</span>
                    <span className="font-bold text-charcoal">
                        {contents.reduce((sum, c) => sum + c.quantity, 0)} adet
                    </span>
                </div>
            </div>
        </div>
    );
};
